import { AxiosRequestConfig } from 'axios';
import { BILLING_API, CARDS, CORE_API, NODE_API } from 'config';

/**
 * The API base URLs whose auth failures we know how to interpret.
 *
 * Two jobs:
 *   1. Tell the session triage which backend answered, because the same status
 *      means different things per backend (Core v2/v3 answer 403 for a dead
 *      token; the node services answer 404).
 *   2. Act as an allowlist. The interceptor is also installed on the shared
 *      `axios` default instance, which third-party code may use, so anything
 *      that does not resolve to a known host is ignored outright.
 */
export const API_HOSTS = {
  core: CORE_API.url,
  nodeLegacy: NODE_API.legacy_url,
  dashboards: NODE_API.dashboards,
  healthRecords: NODE_API.healthRecords,
  members: NODE_API.members,
  onboarding: NODE_API.onboarding,
  reviewList: NODE_API.rl4_url,
  schedule: NODE_API.schedule,
  appointments: NODE_API.appointments,
  billing: BILLING_API.url,
  content: CARDS.contentURL,
};

export type ApiHostKey = keyof typeof API_HOSTS;

export interface ResolvedRequest {
  host: ApiHostKey | null;
  /** The path after the matched base URL. Never leaves this module's callers. */
  path: string;
}

// Same rules axios 0.19 applies in its adapter (lib/core/buildFullPath.js): the
// `config.url` an error carries is still relative to `baseURL`.
const isAbsoluteURL = (url: string) => /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);

const combineURLs = (baseURL: string, relativeURL: string) =>
  relativeURL ? `${baseURL.replace(/\/+$/, '')}/${relativeURL.replace(/^\/+/, '')}` : baseURL;

export const buildFullPath = (baseURL: string | undefined, requestedURL: string | undefined) => {
  const url = requestedURL ?? '';
  if (baseURL && !isAbsoluteURL(url)) {
    return combineURLs(baseURL, url);
  }
  return url;
};

const trimTrailingSlashes = (url: string) => url.replace(/\/+$/, '');

/**
 * Which known backend a request was aimed at, or null if we do not recognise it.
 *
 * Matches on the longest configured base URL so overlapping prefixes resolve to
 * the more specific entry, and only on a path boundary so `https://api.x` does
 * not claim `https://api.x.evil`.
 */
export const resolveApiHost = (config?: AxiosRequestConfig): ResolvedRequest => {
  const absoluteUrl = config ? buildFullPath(config.baseURL, config.url) : '';
  const resolved: ResolvedRequest = { host: null, path: '' };
  if (!absoluteUrl) {
    return resolved;
  }

  let matchedLength = -1;
  (Object.keys(API_HOSTS) as ApiHostKey[]).forEach((key) => {
    const configured = API_HOSTS[key];
    const base = configured ? trimTrailingSlashes(configured) : '';
    if (!base || base.length <= matchedLength || !absoluteUrl.startsWith(base)) {
      return;
    }
    const rest = absoluteUrl.slice(base.length);
    if (rest && !/^[/?#]/.test(rest)) {
      return;
    }
    resolved.host = key;
    resolved.path = rest;
    matchedLength = base.length;
  });

  return resolved;
};
